/**
 * Constantes de configuração do Auth Timing Guard
 *
 * Define os valores compartilhados para controle de tempo de autenticação,
 * garantindo que todos os serviços e componentes utilizem os mesmos limites
 * e códigos de erro para comportamento consistente.
 *
 * @module auth.constants
 */

/**
 * Objeto com constantes do Auth Timing Guard
 * @type {Readonly<{AUTH_TIMEOUT: number, AUTH_TIMEOUT_ERROR: string, MAX_TRANSITION_TIME: number}>}
 * @property {number} AUTH_TIMEOUT - Timeout máximo para espera de auth em milissegundos (10s)
 * @property {string} AUTH_TIMEOUT_ERROR - Código de erro para timeout de autenticação
 * @property {number} MAX_TRANSITION_TIME - Tempo máximo de transição loading→conteúdo em milissegundos (200ms)
 */
export const AUTH_CONSTANTS = Object.freeze({
  /** Timeout máximo para espera de auth (ms) */
  AUTH_TIMEOUT: 10000,
  /** Código de erro para timeout de auth */
  AUTH_TIMEOUT_ERROR: 'AUTH_TIMEOUT',
  /** Tempo máximo de transição loading→conteúdo (ms) */
  MAX_TRANSITION_TIME: 200
})

/**
 * Cria erro de timeout de autenticação identificável.
 * Utilizado quando o estado de auth não é resolvido dentro do prazo de 10 segundos,
 * permitindo que consumidores identifiquem o tipo de erro via propriedade `code`.
 *
 * @returns {Error} Erro com code='AUTH_TIMEOUT' e timeout=10000
 * @example
 * try {
 *   await waitForAuthWithTimeout()
 * } catch (error) {
 *   if (error.code === AUTH_CONSTANTS.AUTH_TIMEOUT_ERROR) {
 *     // Tratar timeout de autenticação
 *   }
 * }
 */
export function createAuthTimeoutError () {
  const error = new Error(
    'Timeout de autenticação: o estado de auth não foi resolvido em 10 segundos'
  )
  error.code = AUTH_CONSTANTS.AUTH_TIMEOUT_ERROR
  error.timeout = AUTH_CONSTANTS.AUTH_TIMEOUT
  return error
}
